/**
 * DietPlanScreen.js — Diet Plan Screen (post-Loading, UC-02 step 7)
 *
 * Not one of the 12 numbered wizard screens and not built on
 * AssessmentStepShell — this is the destination screen, not a wizard
 * step, so there's no step counter or Previous/Next footer.
 *
 * Per FSD: "Displays the Health Summary dashboard (Calories, Protein,
 * Water, BMI, Goal), meal cards for each slot, daily tips, and the
 * disclaimer. Each meal card supports Regenerate (UC-03), Replace Food
 * (UC-04), and expand-for-recipe interactions. Bottom actions: Download
 * PDF (UC-05), Email Plan (UC-06), Share."
 *
 * "If risk_flag = true for the session, a professional-consultation
 * notice must render above the meal cards and cannot be dismissed
 * without acknowledgement (single 'I understand' button, not a silent
 * auto-dismiss)."
 *
 * UC-03 (Regenerate)/UC-04 (Replace Food) don't have a real backend
 * contract yet, so both use the mock helpers in DietPlanScreen.helpers.js
 * — swap those for real API calls once UC-03/UC-04 endpoints exist; the
 * component logic (loading state per card, optimistic-safe update,
 * regeneration-limit tracking, rebalance/warning handling) won't need to
 * change, only what's inside handleRegenerate/handleSelectSubstitute.
 *
 * FSD v2.1 gap closure applied here:
 * - DEC-15: max 3 regenerations per meal, 10 per plan/session, enforced
 *   client-side (mirroring the server-side limit) with an explanatory
 *   message once hit instead of a silent/failed retry.
 * - DEC-16: replace-ingredient responses can carry an auto-rebalanced
 *   sibling meal or a "couldn't safely rebalance" warning; both are now
 *   applied/surfaced instead of being dropped on the floor.
 * - DEC-19 / Policy §19: Share no longer falls back to copying the page
 *   URL (this screen's URL is tied to a private session/health record) —
 *   it copies a plain-text plan summary instead.
 *
 * Not addressed here (flagging for backend/product coordination rather
 * than guessing): FSD v2.1 DEC-13 closes plan length at a 7-day plan with
 * day-by-day structure, but `plan.mealSlots` here is still a flat,
 * single-day list with no day grouping. That's a plan-shape change that
 * has to come from whatever produces `plan` (LoadingScreen/backend)
 * before this screen can render day tabs — worth a decision on whether
 * this screen gets one `plan` per day or the full 7-day plan at once.
 *
 * v1.1: added a `profile` prop (Name/Age/Weight from Screen 2) that flows
 * into the downloaded PDF as a Patient Info bar and drives the filename
 * (e.g. "Sarah_Kapoor_Diet_Plan.pdf" instead of "diet-plan-<sessionId>.pdf").
 * See DietPlanScreen.helpers.js for buildPdfFileName/toTitleCase and the
 * new shared src/components/common/colors.js palette.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import useDietPlanAnalytics from "./useDietPlanAnalytics";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/DietPlan/DietPlanScreen.js, same
// depth as DietPlanScreen.styles.js's "../../../common/colors" import —
// adjust the leading "../" count if this screen moves.
import yogaShastraLogo from "../../../../assets/YogashastraClear.png";
import {
  RISK_NOTICE_TEXT,
  DEFAULT_DISCLAIMER,
  HEALTH_SUMMARY_FIELDS,
  NO_SUBSTITUTES_MESSAGE,
  NO_ALTERNATIVE_MESSAGE,
  MAX_REGENERATIONS_PER_MEAL,
  MAX_TOTAL_REGENERATIONS_PER_PLAN,
  REGEN_LIMIT_PER_MEAL_MESSAGE,
  REGEN_LIMIT_TOTAL_MESSAGE,
  NO_SAFE_REBALANCE_MESSAGE,
  REBALANCE_APPLIED_MESSAGE,
} from "./DietPlanScreen.constants";
import {
  filterSubstitutionChain,
  mockRegenerateMeal,
  mockReplaceIngredient,
  generatePlanPdf,
  buildShareSummaryText,
  buildPdfFileName,
} from "./DietPlanScreen.helpers";
import {
  Screen,
  Header,
  Title,
  Subtitle,
  RiskNotice,
  RiskNoticeTitle,
  RiskNoticeText,
  AcknowledgeButton,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  MealList,
  MealCard,
  MealHeader,
  MealSlotLabel,
  MealName,
  MealMacros,
  MealActions,
  IconButton,
  RecipePanel,
  IngredientRow,
  SubstituteRow,
  SubstituteChip,
  StepsList,
  InlineNotice,
  TipsList,
  Disclaimer,
  ActionBar,
  PrimaryActionButton,
  SecondaryActionButton,
} from "./DietPlanScreen.styles";

/**
 * @param {object}   props
 * @param {string}   props.sessionId
 * @param {object}   props.plan          - The resolved plan from
 *   LoadingScreen: { riskFlag, healthSummary, mealSlots, dailyTips,
 *   disclaimer, regenerationsUsed }. `regenerationsUsed` and each meal's
 *   `regenerationsUsed` are optional — when the backend tracks the DEC-15
 *   limit server-side, seed the UI counters from there so a page refresh
 *   doesn't quietly reset the user's remaining regenerations.
 * @param {string[]} [props.dislikes]    - Screen 9 data, used to filter
 *   ingredient substitution chains per UC-04.
 * @param {object}   [props.mealContext] - Output of
 *   MealEngine.buildMealContext(loadingPayload), built once by
 *   DietPlannerWizard.js from the same assessment payload LoadingScreen
 *   used to generate the plan. Only consumed by this screen's *local
 *   mock* fallbacks for onRegenerateMeal/onReplaceIngredient (see below)
 *   so they can pick a still diet-type/gluten/dislike-eligible dish from
 *   MealCatalog.js — a real backend wouldn't need this passed over the
 *   wire, since it already has the session's stored profile.
 * @param {object}   [props.profile]     - `{ name, age, weight,
 *   weightUnit }` from Screen 2 (Basic Information). Kept as a sibling
 *   prop rather than folded into `plan` — identity data and the
 *   generated plan are logically separate, same reasoning as `dislikes`
 *   above. Used to render the Patient Info bar on the downloaded PDF
 *   (UC-05) and to name the file, e.g. "Sarah_Kapoor_Diet_Plan.pdf". All
 *   fields optional; anything missing is simply omitted from the PDF.
 * @param {function} [props.onDownloadPdf]  - `() => Promise<void>` (UC-05).
 *   Defaults to a local mock.
 * @param {function} [props.onEmailPlan]    - Opens the Email Popup (UC-06).
 *   Required — DietPlannerWizard wires this to its Email Popup state.
 * @param {function} [props.onRegenerateMeal] - `(meal) => Promise<{meal,
 *   warning}>` (UC-03). Defaults to a local mock.
 * @param {function} [props.onReplaceIngredient] - `(meal, ingredientId,
 *   substitute) => Promise<{meal, rebalancedMeals, warning}>` (UC-04).
 *   Defaults to a local mock.
 */
const DietPlanScreen = ({
  sessionId,
  plan,
  dislikes = [],
  profile = {},
  mealContext = {},
  onDownloadPdf,
  onEmailPlan,
  onRegenerateMeal,
  onReplaceIngredient,
}) => {
  const analytics = useDietPlanAnalytics();
  const riskFlag = Boolean(plan?.riskFlag);
  const hasPlanContent = Boolean(plan && (plan.healthSummary || (plan.mealSlots && plan.mealSlots.length > 0)));

  const [meals, setMeals] = useState(plan?.mealSlots ?? []);
  const [expandedId, setExpandedId] = useState(null);
  const [openSubstituteFor, setOpenSubstituteFor] = useState(null); // ingredientId
  const [mealBusy, setMealBusy] = useState({}); // { [mealId]: 'regenerating' | 'replacing' }
  const [mealNotice, setMealNotice] = useState({}); // { [mealId]: string }
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);
  const [pdfState, setPdfState] = useState("idle"); // "idle" | "working" | "done" | "error"

  // FSD v2.1 DEC-15: per-meal (3) and per-plan (10) regeneration limits.
  // Seeded from the plan/meal props when the backend supplies a running
  // count, so a remount doesn't hand back regenerations the server would
  // still reject.
  const [mealRegenCounts, setMealRegenCounts] = useState(() => {
    const counts = {};
    (plan?.mealSlots ?? []).forEach((m) => {
      counts[m.id] = m.regenerationsUsed ?? 0;
    });
    return counts;
  });
  const [totalRegenCount, setTotalRegenCount] = useState(() => plan?.regenerationsUsed ?? 0);

  useEffect(() => {
    analytics.trackStepView(sessionId, riskFlag);
    // Fire once per mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `useState(plan?.mealSlots ?? [])` above only reads `plan` on the very
  // first render — if this screen ever mounts before `plan` is fully
  // populated (or a memoized parent re-renders it with a new plan object
  // without a remount), `meals` would otherwise be stuck on whatever it
  // saw first. Resyncing here means a later, correct `plan` prop always
  // wins.
  useEffect(() => {
    setMeals(plan?.mealSlots ?? []);
  }, [plan]);

  // Same resync rationale as above, applied to the DEC-15 regeneration
  // counters — a genuinely new plan (new session, or a refreshed plan
  // object from the backend) should reset how many regenerations are
  // left, not carry over a stale count from whatever plan mounted first.
  useEffect(() => {
    const counts = {};
    (plan?.mealSlots ?? []).forEach((m) => {
      counts[m.id] = m.regenerationsUsed ?? 0;
    });
    setMealRegenCounts(counts);
    setTotalRegenCount(plan?.regenerationsUsed ?? 0);
  }, [plan]);

  useEffect(() => {
    if (!hasPlanContent) {
      // eslint-disable-next-line no-console
      console.warn(
        "[DietPlanScreen] Rendered without a populated plan — expected `plan.healthSummary` and " +
          "`plan.mealSlots`, got:",
        plan
      );
    }
  }, [hasPlanContent, plan]);

  const healthSummary = plan?.healthSummary ?? {};
  const dailyTips = plan?.dailyTips ?? [];
  const disclaimer = plan?.disclaimer || DEFAULT_DISCLAIMER;

  const handleAcknowledgeRisk = useCallback(() => {
    setRiskAcknowledged(true);
    analytics.trackRiskAcknowledged(sessionId);
  }, [analytics, sessionId]);

  const toggleExpand = useCallback(
    (mealId) => {
      setExpandedId((prev) => {
        const next = prev === mealId ? null : mealId;
        analytics.trackMealExpand(sessionId, mealId, next === mealId);
        return next;
      });
      setOpenSubstituteFor(null);
    },
    [analytics, sessionId]
  );

  const handleRegenerate = useCallback(
    async (meal) => {
      const mealCount = mealRegenCounts[meal.id] ?? 0;

      // FSD v2.1 DEC-15: stop at the limit with an explanation rather than
      // letting the click go through to a backend that will reject it (or,
      // with the mock, regenerate indefinitely).
      if (mealCount >= MAX_REGENERATIONS_PER_MEAL) {
        analytics.trackRegenerateLimitReached(sessionId, meal.id, "per_meal");
        setMealNotice((prev) => ({ ...prev, [meal.id]: REGEN_LIMIT_PER_MEAL_MESSAGE }));
        return;
      }
      if (totalRegenCount >= MAX_TOTAL_REGENERATIONS_PER_PLAN) {
        analytics.trackRegenerateLimitReached(sessionId, meal.id, "per_plan");
        setMealNotice((prev) => ({ ...prev, [meal.id]: REGEN_LIMIT_TOTAL_MESSAGE }));
        return;
      }

      analytics.trackRegenerateClick(sessionId, meal.id);
      setMealBusy((prev) => ({ ...prev, [meal.id]: "regenerating" }));
      setMealNotice((prev) => ({ ...prev, [meal.id]: null }));

      // mealContext is only threaded into the *mock* — a real
      // onRegenerateMeal already knows the session's profile server-side
      // and keeps the simpler `(meal) => Promise<{meal, warning}>` contract
      // documented above.
      const regenerate = onRegenerateMeal ?? ((m) => mockRegenerateMeal(m, mealContext));
      try {
        const result = await regenerate(meal);
        // Tolerate either the new { meal, warning } shape or a bare meal,
        // in case a caller hasn't updated onRegenerateMeal yet.
        const updated = result?.meal ?? result;
        const warning = result?.warning ?? null;

        setMeals((prev) => prev.map((m) => (m.id === meal.id ? updated : m)));
        setMealRegenCounts((prev) => ({ ...prev, [meal.id]: mealCount + 1 }));
        setTotalRegenCount((prev) => prev + 1);
        analytics.trackRegenerateResult(sessionId, meal.id, true);

        if (warning) {
          setMealNotice((prev) => ({ ...prev, [meal.id]: warning }));
          analytics.trackRebalanceWarning(sessionId, meal.id);
        }
      } catch (err) {
        analytics.trackRegenerateResult(sessionId, meal.id, false);
        setMealNotice((prev) => ({ ...prev, [meal.id]: NO_ALTERNATIVE_MESSAGE }));
      } finally {
        setMealBusy((prev) => ({ ...prev, [meal.id]: null }));
      }
    },
    [analytics, sessionId, onRegenerateMeal, mealRegenCounts, totalRegenCount]
  );

  const handleOpenSubstitutes = useCallback(
    (meal, ingredient) => {
      setOpenSubstituteFor((prev) => (prev === ingredient.id ? null : ingredient.id));
      analytics.trackReplaceIngredientOpen(sessionId, meal.id, ingredient.id);
    },
    [analytics, sessionId]
  );

  const handleSelectSubstitute = useCallback(
    async (meal, ingredient, substitute) => {
      analytics.trackReplaceIngredientSelect(sessionId, meal.id, ingredient.id, substitute);
      setMealBusy((prev) => ({ ...prev, [meal.id]: "replacing" }));

      const replace = onReplaceIngredient ?? mockReplaceIngredient;
      try {
        const result = await replace(meal, ingredient.id, substitute);
        // Tolerate either the new { meal, rebalancedMeals, warning } shape
        // or a bare meal, in case a caller hasn't updated
        // onReplaceIngredient yet.
        const updated = result?.meal ?? result;
        const rebalancedMeals = result?.rebalancedMeals ?? [];
        const warning = result?.warning ?? null;

        setMeals((prev) =>
          prev.map((m) => {
            if (m.id === meal.id) return updated;
            const rebalancedMatch = rebalancedMeals.find((rb) => rb.id === m.id);
            return rebalancedMatch ?? m;
          })
        );

        // FSD v2.1 DEC-16: a replace can (a) succeed cleanly, (b) trigger
        // an auto-rebalance of another eligible meal to stay within
        // ±5%/protein-floor, or (c) fail to find a safe rebalance — in
        // which case we warn rather than silently accepting a swap that
        // pushed the day out of range. Only one of these applies.
        if (warning) {
          setMealNotice((prev) => ({ ...prev, [meal.id]: warning || NO_SAFE_REBALANCE_MESSAGE }));
          analytics.trackRebalanceWarning(sessionId, meal.id);
        } else if (rebalancedMeals.length > 0) {
          setMealNotice((prev) => ({ ...prev, [meal.id]: REBALANCE_APPLIED_MESSAGE }));
        }
      } finally {
        setMealBusy((prev) => ({ ...prev, [meal.id]: null }));
        setOpenSubstituteFor(null);
      }
    },
    [analytics, sessionId, onReplaceIngredient]
  );

  const handleDownloadPdf = useCallback(async () => {
    analytics.trackDownloadPdf(sessionId);
    setPdfState("working");
    try {
      if (onDownloadPdf) {
        await onDownloadPdf(plan);
      } else {
        // Real client-side PDF via jsPDF (npm install jspdf) — replace
        // with a call to the real UC-05 PDF Generation Service once that
        // backend exists; `onDownloadPdf` above is the swap-in point.
        // `logoUrl` is best-effort (see generatePlanPdf) — a broken
        // import here still produces a PDF, just without the logo.
        // `profile` (Name/Age/Weight) renders as a Patient Info bar on
        // the PDF and drives the filename below.
        const doc = await generatePlanPdf({ ...plan, mealSlots: meals }, { logoUrl: yogaShastraLogo, profile });
        doc.save(buildPdfFileName(profile.name));
      }
      setPdfState("done");
      setTimeout(() => setPdfState("idle"), 2500);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[DietPlanScreen] PDF generation failed:", err);
      setPdfState("error");
    }
  }, [analytics, sessionId, onDownloadPdf, plan, meals, profile]);

  const handleEmailPlanClick = useCallback(() => {
    analytics.trackEmailPlanOpen(sessionId);
    onEmailPlan?.();
  }, [analytics, sessionId, onEmailPlan]);

  const handleShare = useCallback(async () => {
    // FSD v2.1 DEC-19 / Policy §19: "Use native device/browser share of
    // the current PDF or user-approved plan summary. Do not create public
    // health-data URLs." Never pass `url` here — this screen's URL is
    // tied to a private session/health record.
    const summaryText = buildShareSummaryText({ ...plan, mealSlots: meals });

    if (navigator.share) {
      try {
        await navigator.share({ title: "My Diet Plan", text: summaryText });
        analytics.trackShare(sessionId, "native_share");
        return;
      } catch (err) {
        // User cancelled the native share sheet — no fallback needed.
        return;
      }
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(summaryText);
      analytics.trackShare(sessionId, "clipboard");
    }
  }, [analytics, sessionId, plan, meals]);

  const showRiskNotice = riskFlag && !riskAcknowledged;

  if (!hasPlanContent) {
    return (
      <Screen>
        <Header>
          <Title>Preparing your plan…</Title>
          <Subtitle>
            This should only take a moment. If it doesn't update, head back to Summary and try
            generating your plan again.
          </Subtitle>
        </Header>
      </Screen>
    );
  }

  return (
    <Screen>
      <Header>
        <Title>Your personalized diet plan</Title>
        <Subtitle>Built from everything you told us — regenerate or swap anything that doesn't fit.</Subtitle>
      </Header>

      {showRiskNotice && (
        <RiskNotice role="alert">
          <RiskNoticeTitle>Please review with a professional</RiskNoticeTitle>
          <RiskNoticeText>{RISK_NOTICE_TEXT}</RiskNoticeText>
          <AcknowledgeButton type="button" onClick={handleAcknowledgeRisk}>
            I understand
          </AcknowledgeButton>
        </RiskNotice>
      )}

      <SummaryGrid>
        {HEALTH_SUMMARY_FIELDS.map((field) => (
          <SummaryCard key={field.key}>
            <SummaryValue>
              {healthSummary[field.key] ?? "—"}
              {field.unit && healthSummary[field.key] ? (
                <span style={{ fontSize: "0.6em", fontWeight: 600, marginLeft: 2 }}>
                  {field.unit.split("/")[0]}
                </span>
              ) : null}
            </SummaryValue>
            <SummaryLabel>{field.label}</SummaryLabel>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <MealList>
        {meals.map((meal) => {
          const isExpanded = expandedId === meal.id;
          const busy = mealBusy[meal.id];
          const notice = mealNotice[meal.id];
          const mealRegenCount = mealRegenCounts[meal.id] ?? 0;
          const regenLimitReached =
            mealRegenCount >= MAX_REGENERATIONS_PER_MEAL || totalRegenCount >= MAX_TOTAL_REGENERATIONS_PER_PLAN;

          return (
            <MealCard key={meal.id}>
              <MealHeader>
                <div>
                  <MealSlotLabel>
                    {meal.slotLabel} {meal.time ? `· ${meal.time}` : ""}
                  </MealSlotLabel>
                  <MealName>{meal.name}</MealName>
                  <MealMacros>
                    {meal.calories} kcal · {meal.proteinG}g protein · {meal.carbsG}g carbs · {meal.fatG}g fat
                  </MealMacros>
                </div>
                <MealActions>
                  <IconButton type="button" onClick={() => toggleExpand(meal.id)}>
                    {isExpanded ? "Hide recipe" : "View recipe"}
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => handleRegenerate(meal)}
                    disabled={busy === "regenerating" || regenLimitReached}
                  >
                    {busy === "regenerating" ? "Regenerating…" : "Regenerate"}
                  </IconButton>
                </MealActions>
              </MealHeader>

              {notice && <InlineNotice>{notice}</InlineNotice>}

              {isExpanded && (
                <RecipePanel>
                  {(meal.ingredients ?? []).map((ingredient) => {
                    const substitutes = filterSubstitutionChain(
                      (ingredient.substitutionChain || []).filter((s) => s !== ingredient.name),
                      dislikes
                    );
                    const showingSubstitutes = openSubstituteFor === ingredient.id;

                    return (
                      <div key={ingredient.id}>
                        <IngredientRow>
                          <span>{ingredient.name}</span>
                          <IconButton
                            type="button"
                            onClick={() => handleOpenSubstitutes(meal, ingredient)}
                            disabled={busy === "replacing"}
                          >
                            Replace
                          </IconButton>
                        </IngredientRow>
                        {showingSubstitutes && (
                          <SubstituteRow>
                            {substitutes.length > 0 ? (
                              substitutes.map((sub) => (
                                <SubstituteChip
                                  key={sub}
                                  type="button"
                                  onClick={() => handleSelectSubstitute(meal, ingredient, sub)}
                                >
                                  {sub}
                                </SubstituteChip>
                              ))
                            ) : (
                              <InlineNotice>{NO_SUBSTITUTES_MESSAGE}</InlineNotice>
                            )}
                          </SubstituteRow>
                        )}
                      </div>
                    );
                  })}
                  {(meal.recipeSteps ?? []).length > 0 && (
                    <StepsList>
                      {meal.recipeSteps.map((step, i) => (
                        // Steps are static copy for a given meal, not a
                        // reorderable/keyed list — index is stable here.
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={i}>{step}</li>
                      ))}
                    </StepsList>
                  )}
                </RecipePanel>
              )}
            </MealCard>
          );
        })}
      </MealList>

      {dailyTips.length > 0 && (
        <TipsList>
          {dailyTips.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </TipsList>
      )}

      <Disclaimer>{disclaimer}</Disclaimer>

      <ActionBar>
        <PrimaryActionButton type="button" onClick={handleDownloadPdf} disabled={pdfState === "working"}>
          {pdfState === "working" ? "Preparing PDF…" : pdfState === "done" ? "Downloaded ✓" : "Download PDF"}
        </PrimaryActionButton>
        <SecondaryActionButton type="button" onClick={handleEmailPlanClick}>
          Email Plan
        </SecondaryActionButton>
        <SecondaryActionButton type="button" onClick={handleShare}>
          Share
        </SecondaryActionButton>
      </ActionBar>
    </Screen>
  );
};

DietPlanScreen.displayName = "DietPlanScreen";
export default React.memo(DietPlanScreen);